import { type ReactElement } from "react";
import {
  type GenericTableCell,
  type GenericTableColumn,
  type GenericTableParams,
  type GenericTableRow,
} from "@playbookai/widget-schemas";
import {
  DataTable,
  type DataTableColumn,
} from "../../molecules/DataTable/DataTable.js";
import type { WidgetBodyProps } from "../types.js";

const PREVIEW_COLUMNS = 4;
const PREVIEW_ROWS = 5;

export function GenericTableWidget({
  data,
}: WidgetBodyProps<GenericTableParams>): ReactElement {
  const visibleColumns = data.columns.slice(0, PREVIEW_COLUMNS);
  const visibleRows = data.rows.slice(0, PREVIEW_ROWS);
  const columns: DataTableColumn<GenericTableRow>[] = visibleColumns.map(
    (column) => ({
      id: column.id,
      header: column.label,
      cell: (row) => formatGenericTableCell(row.cells[column.id], column),
    }),
  );
  const hiddenColumns = Math.max(
    0,
    data.columns.length - visibleColumns.length,
  );
  const hiddenRows = Math.max(0, data.rows.length - visibleRows.length);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <h3 className="truncate text-sm font-semibold text-pb-text">
        {data.title}
      </h3>
      {data.description ? (
        <p className="line-clamp-2 text-xs text-pb-text-muted">
          {data.description}
        </p>
      ) : null}
      {columns.length > 0 ? (
        <DataTable
          columns={columns}
          data={visibleRows}
          getRowId={(row) => row.id}
          density="compact"
          responsive
          emptyState={
            <span className="text-xs text-pb-text-muted">No rows yet</span>
          }
        />
      ) : (
        <div className="flex flex-1 items-center justify-center rounded-md border border-dashed border-pb-border text-sm text-pb-text-muted">
          Add columns in the expanded table editor
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-pb-text-muted">
        <span>
          {data.rows.length} rows · {data.columns.length} columns
        </span>
        {hiddenRows || hiddenColumns ? (
          <span>
            +{" "}
            {[
              hiddenRows ? `${hiddenRows} rows` : null,
              hiddenColumns ? `${hiddenColumns} columns` : null,
            ]
              .filter((value): value is string => value !== null)
              .join(" · ")}{" "}
            more
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function formatGenericTableCell(
  cell: GenericTableCell | undefined,
  column: GenericTableColumn,
): string {
  if (!cell || cell.type === "empty") return "—";
  if (
    cell.type === "text" ||
    cell.type === "longText" ||
    cell.type === "date"
  ) {
    return cell.value;
  }
  if (cell.type === "number")
    return `${cell.value}${column.unit ? ` ${column.unit}` : ""}`;
  if (cell.type === "boolean") return cell.value ? "Yes" : "No";
  if (cell.type === "user") {
    return cell.userId ? cell.displayName : `${cell.displayName} (unresolved)`;
  }
  const options = new Map(
    (column.options ?? []).map((option) => [option.id, option.label]),
  );
  if (cell.type === "singleSelect")
    return options.get(cell.optionId) ?? "Unknown option";
  return cell.optionIds
    .map((id) => options.get(id) ?? "Unknown option")
    .join(", ");
}
